import "./style.scss";
import React, { useEffect, useState, useRef, useCallback } from "react";
import imageCompression from "browser-image-compression";
import { uploadImageApi } from "services/NexiDigitalAPI";
import ImageCropList from "components/ImageCropList";

type Props = {
  className?: string;
  reference?: any;
  data: any;
  sendData?: any;
  defaultPrefixName?: string;
  disabled?: boolean;
  showMessageRequired?: boolean;
  uploadImageFeedback?: (e: EventTarget & HTMLInputElement) => void;
};

function ContentImageUpload({
  className = "",
  reference,
  data,
  sendData,
  defaultPrefixName,
  disabled,
  showMessageRequired = false,
  uploadImageFeedback,
}: Props) {
  const imagesToUpload: any = useRef([]);
  const [defaultUploadedImage, setDefaultUploadedImage] = useState(null);
  const [carregaImagem, setCarregaImagem] = useState(false);
  const baseImage = useRef(
    data.image !== null && data.image !== ""
      ? data.image
          .split("/")
          .slice(0, data.image.length - 1)
          .splice(-1, 1)
          .join()
          .split(".")[0]
      : defaultPrefixName
      ? `${defaultPrefixName}-${Date.now()}`
      : `image-${Date.now()}`
  );

  // const [saveImageBtn, setSaveImageBtn] = useState(false);
  const [imagemUploadStatus, setImagemUploadStatus] = useState(false);
  const [mensagemImagemUpload, setMensagemImagemUpload] = useState("");
  const editContent = useRef(data.image === null || data.image === "" ? false : true);

  const [imgErro, setImgErro] = useState(null);

  const contentData = useRef(data);

  const checkImage = (url) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = url;
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
    });
  };

  const checkImgErro = useCallback(async (url) => {
    const valid: any = await checkImage(url);
    setImgErro(!valid);
  }, []);

  useEffect(() => {
    if (reference) {
      reference.current = {
        uploadImagem,
        onCropImage,
        uploadDefaultImage,
        imagesToUpload,
      };
    }

    if (data.image && imgErro === null) {
      checkImgErro(data.image);
    }

    if (imgErro) {
      baseImage.current = `image-${Date.now()}`;
    }
  }, [reference, data, checkImgErro, imgErro]);

  const uploadImagem = async (e: any) => {
    // setSaveImageBtn(false);

    if (e) {
      e.preventDefault();
    }

    setCarregaImagem(true);
    setMensagemImagemUpload("");

    // const options = {
    //   maxSizeMB: 0.9,
    //   maxWidthOrHeight: 1920,
    //   fileType: "image/webp",
    // };

    const options = {
      maxSizeMB: 2, //
      maxWidthOrHeight: 1920, // mantém a resolução original
      fileType: "image/blob", // mantém o tipo original (jpeg/png/etc)
      useWebWorker: true, // processamento em background
      initialQuality: 1.0, // qualidade máxima
      alwaysKeepResolution: true, // evita reduzir resolução
      preserveExif: true, // preserva metadados, se quiser manter
    };

    const baseImageObject = imagesToUpload.current.filter(
      (x: any) => x.imageFormatName.indexOf("-format-landscape-large") !== -1
    );

    if (baseImageObject.length > 0) {
      imagesToUpload.current.push({
        imageFormatName: baseImage.current,
        file: baseImageObject[0].file,
        aspectRatio: baseImageObject[0].aspectRatio,
        blob: baseImageObject[0].blob,
      });
    }

    const promise = new Promise(async (resolve, reject) => {
      if (imagesToUpload.current.length === 0) {
        resolve(data.image || null);
      }

      while (imagesToUpload.current.length > 0) {
        const currentImageObject = imagesToUpload.current.shift();
        const imageFile = currentImageObject.file;

        console.log("currentImageObject", currentImageObject);

        try {
          const compressedFile = await imageCompression(imageFile, options);
          const filename = currentImageObject.imageFormatName;
          const dataFormat = new FormData();

          if (compressedFile) {
            dataFormat.append("fileName", filename);
            dataFormat.append("fileData", compressedFile);
            uploadImageApi(dataFormat)
              .then((response) => {
                if (response.data?.error) {
                  setCarregaImagem(false);
                  setImagemUploadStatus(false);
                  setMensagemImagemUpload("Erro ao fazer upload do arquivo");
                } else {
                  if (baseImageObject.length > 0) {
                    if (filename === `${baseImage.current}`) {
                      resolve(response.data?.location);
                    }
                  } else if (imagesToUpload.current.length === 0) {
                    resolve(data.image || null);
                  }
                }
              })
              .catch((error) => {
                // If another error
                console.log("error", error);
                setCarregaImagem(false);
                setImagemUploadStatus(false);
                setMensagemImagemUpload("Erro ao fazer upload do arquivo");
              });
          }
        } catch (error) {
          console.log(error);
        }

        if (imagesToUpload.current.length === 0) {
          setCarregaImagem(false);
          setImagemUploadStatus(true);
          setMensagemImagemUpload("Imagens atualizadas com successo.");
        }
      }
    });

    return promise;
  };

  const onCropImage = async (croppedImage: any) => {
    imagesToUpload.current = imagesToUpload.current.filter(
      (x: any) => x.imageFormatName !== croppedImage.imageFormatName
    );

    imagesToUpload.current.push(croppedImage);

    // if (imagesToUpload.current.length > 0) {
    //   setSaveImageBtn(true);
    // }
  };

  const uploadDefaultImage = async (arquivo: any) => {
    const imageFile = arquivo.files[0];
    setDefaultUploadedImage(window.URL.createObjectURL(imageFile));
  };

  return (
    <div className={`pctContentImageUpload ${className}`}>
      <div className="node-images-crop-body">
        <div className="crop-images-header">
          <label className="content-page-file-button">
            Escolher arquivo
            <input
              disabled={disabled}
              type="file"
              className="form-control-file"
              id="imagem"
              accept="image/png, image/jpeg, image/gif"
              onChange={(e) => {
                uploadDefaultImage(e.target);
                if (uploadImageFeedback) {
                  uploadImageFeedback(e.target);
                }
              }}
            />
          </label>

          <p className="infoSize">
            Tamanho recomendado para melhor adaptação em todos os formatos:<b>1920 x 900px</b>.
          </p>
          {showMessageRequired && <span className="invalid-feedback">A imagem é obrigatória.</span>}
        </div>

        {/* TODO: EDIT CONTENT */}
        {editContent.current && (
          <p>
            <b>Atenção:</b> Ao selecionar o arquivo todos os formatos serão substituídos pela nova
            imagem selecionada. Se você pretende apenas trocar um dos formatos você pode editar
            diretamente nele. Após qualquer alteração é necessário clicar no botão salvar.
          </p>
        )}

        <ImageCropList
          contentData={contentData}
          defaultUploadedImage={defaultUploadedImage}
          baseImage={baseImage}
          onCropImage={onCropImage}
          carregaImagem={carregaImagem}
        />

        <div className="content-page-img-feedback">
          {imagemUploadStatus ? (
            <span style={{ color: "green", marginTop: 5 }}>{mensagemImagemUpload}</span>
          ) : (
            <span style={{ color: "red", marginTop: 5 }}>{mensagemImagemUpload}</span>
          )}
        </div>
      </div>
    </div>
  );
}

export default ContentImageUpload;
